import { Task, TaskHistoryEntry } from '../types';
import { buildTaskHistoryEntries, syncStatusAndCompletedFlag } from '../hooks/project/crud/taskHistoryUtils';
import { isMilestoneOrDeadlineView } from './taskGuards';

export interface PrepareTaskMutationOptions {
  userEmail?: string;
  customColumns?: Array<{ id: string; category?: string }>;
  nowMs?: number;
  nowIso?: string;
}

export interface PreparedTaskMutationResult {
  finalUpdates: Partial<Task>;
  historyEntries: TaskHistoryEntry[];
  mergedHistory: TaskHistoryEntry[] | undefined;
  shouldSpawnRecurrence: boolean;
  isMilestoneCelebration: boolean;
}

/**
 * Pure domain helper that computes the canonical `finalUpdates` for a single task mutation
 * (whether triggered via `updateTask` or `updateTasksMultiple`):
 * 1. Sets `lastContactDate` when transitioning to `'contacted' | 'pending' | 'completed'`.
 * 2. Synchronizes `status` and `completed` flags (including custom column categories).
 * 3. Stops any running timer (`timerStartedAt`) and accumulates `trackedTime` on completion, hold, or archive.
 * 4. Initializes `trackedTime = 0` when starting a timer for the first time.
 * 5. Builds and prepends German audit history entries (capped at 100).
 * 6. Determines whether a recurring task instance should be spawned.
 */
export function prepareTaskMutationUpdates(
  originalTask: Task,
  rawUpdates: Partial<Task>,
  options: PrepareTaskMutationOptions = {}
): PreparedTaskMutationResult {
  const nowMs = options.nowMs ?? Date.now();
  const nowIso = options.nowIso ?? new Date(nowMs).toISOString();
  const userStr = options.userEmail || 'System';

  const finalUpdates: Partial<Task> = { ...rawUpdates };

  // 1. Contact-Vorgang / contact status timestamp
  if (
    finalUpdates.status &&
    ['contacted', 'pending', 'completed'].includes(finalUpdates.status) &&
    !finalUpdates.lastContactDate
  ) {
    finalUpdates.lastContactDate = nowIso;
  }

  // 2. Sync status <-> completed boolean
  syncStatusAndCompletedFlag(originalTask, finalUpdates, options.customColumns);

  // 3. Time tracking: auto-stop timer on completion, hold, or archive
  const isStoppingCondition =
    finalUpdates.completed === true ||
    finalUpdates.status === 'completed' ||
    finalUpdates.status === 'hold' ||
    finalUpdates.archived === true;

  if (isStoppingCondition && originalTask.timerStartedAt) {
    const elapsed = Math.max(0, nowMs - originalTask.timerStartedAt);
    finalUpdates.trackedTime = (originalTask.trackedTime || 0) + elapsed;
    finalUpdates.timerStartedAt = null;
  } else if (finalUpdates.timerStartedAt && !originalTask.timerStartedAt && originalTask.trackedTime === undefined) {
    finalUpdates.trackedTime = 0;
  }

  // 4. Audit history
  const historyEntries = buildTaskHistoryEntries(originalTask, finalUpdates, userStr);
  const mergedHistory =
    historyEntries.length > 0
      ? [...historyEntries, ...(originalTask.history || [])].slice(0, 100)
      : originalTask.history;

  if (historyEntries.length > 0) {
    finalUpdates.history = mergedHistory;
  }

  // 5. Recurrence & celebration flags
  const activeRecurrence =
    finalUpdates.recurrence !== undefined ? finalUpdates.recurrence : originalTask.recurrence;
  const shouldSpawnRecurrence = Boolean(
    finalUpdates.completed &&
      !originalTask.completed &&
      activeRecurrence &&
      activeRecurrence.frequency !== 'none'
  );

  const isMilestoneCelebration = Boolean(
    finalUpdates.completed &&
      !originalTask.completed &&
      isMilestoneOrDeadlineView(originalTask)
  );

  return {
    finalUpdates,
    historyEntries,
    mergedHistory,
    shouldSpawnRecurrence,
    isMilestoneCelebration,
  };
}
